import ResourcePage from './ui/ResourcePage';
import {resourceFetch as apiFetch,reportResourceError} from './app/resourceApi';
import {ResourceEditor,Field,CreatePanel} from './ui';
import React, { useEffect, useState } from 'react';
import { API_BASE, showAppConfirm } from './api';
import { mergeStyles, theme } from './theme';

const LICENSE_CLASSES = ['B', 'C', 'C1', 'BE', 'C1E', 'CE'];

const styles = {
  stack: { display: 'grid', gap: '20px' },
  card: { ...theme.glassCard, padding: '24px', color: 'var(--board-text-strong, #fff)' },
  title: { margin: '0 0 16px', color: 'var(--board-text-strong, #fff)', fontSize: '20px' },
  formGrid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(190px, 1fr))', gap: '10px', alignItems: 'start' },
  input: { ...theme.input, width: '100%' },
  textarea: { ...theme.input, width: '100%', minHeight: '72px', resize: 'vertical' },
  button: { ...theme.button, ...theme.primaryButton },
  tableWrap: { overflowX: 'auto' },
  table: theme.table,
  th: theme.th,
  td: theme.td,
  deleteBtn: { ...theme.button, ...theme.dangerButton, padding: '7px 12px', fontSize: '12px', marginRight: '6px' },
  editBtn: { ...theme.button, ...theme.mutedButton, padding: '7px 12px', fontSize: '12px', marginRight: '6px' },
  saveBtn: { ...theme.button, ...theme.successButton, padding: '7px 12px', fontSize: '12px', marginRight: '6px' },
  mutedBtn: { ...theme.button, ...theme.mutedButton, padding: '7px 12px', fontSize: '12px', marginRight: '6px' },
  licenseWrap: { display: 'flex', flexWrap: 'wrap', gap: '8px' },
  licenseChip: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '6px',
    padding: '7px 10px',
    borderRadius: '999px',
    background: 'var(--board-surface-soft, rgba(255,255,255,0.08))',
    border: '1px solid var(--board-border, rgba(255,255,255,0.15))',
    fontSize: '13px',
  },
  label: { display: 'block', marginBottom: '6px', fontSize: '12px', color: 'var(--board-text-muted, rgba(255,255,255,0.62))' },
  badge: {
    display: 'inline-block',
    marginRight: '5px',
    marginBottom: '4px',
    padding: '4px 8px',
    borderRadius: '999px',
    background: 'var(--ui-secondary)',
    border: '1px solid var(--ui-border)',
    color: 'var(--ui-text)',
    fontSize: '12px',
    fontWeight: 800,
  },
  activeBadge: {
    display: 'inline-block',
    padding: '5px 9px',
    borderRadius: '999px',
    background: 'rgba(34,197,94,0.18)',
    border: '1px solid rgba(74,222,128,0.36)',
    color: 'var(--board-success-text, #dcfce7)',
    fontSize: '12px',
    fontWeight: 800,
  },
  inactiveBadge: {
    display: 'inline-block',
    padding: '5px 9px',
    borderRadius: '999px',
    background: 'rgba(148,163,184,0.16)',
    border: '1px solid rgba(148,163,184,0.34)',
    color: 'var(--board-text-soft, #e2e8f0)',
    fontSize: '12px',
    fontWeight: 800,
  },
  inactiveRow: { opacity: 0.62 },
  activeToggle: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '8px',
    color: 'var(--board-text-soft, rgba(255,255,255,0.82))',
    fontSize: '13px',
    fontWeight: 700,
  },
};

function getLicenseClasses(driver) {
  if (Array.isArray(driver.license_classes)) return driver.license_classes;
  if (driver.license_class) return String(driver.license_class).split(',').map(item => item.trim()).filter(Boolean);
  return [];
}

function Drivers() {
  const [drivers, setDrivers] = useState([]);
  const [form, setForm] = useState({ name: '', phone: '', license_classes: [], notes: '' });
  const [editId, setEditId] = useState(null);
  const [editForm, setEditForm] = useState({ name: '', phone: '', license_classes: [], notes: '', is_active: true });
  const [viewportWidth, setViewportWidth] = useState(() => window.innerWidth);

  const loadDrivers = () => {
    apiFetch(`${API_BASE}/drivers?includeInactive=true`)
      .then(res => res.json())
      .then(data => setDrivers(Array.isArray(data) ? data : [])).catch(reportResourceError);
  };

  useEffect(() => { loadDrivers(); }, []);
  useEffect(() => {
    const handleResize = () => setViewportWidth(window.innerWidth);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const toggleLicense = (state, setter, licenseClass) => {
    setter({
      ...state,
      license_classes: state.license_classes.includes(licenseClass)
        ? state.license_classes.filter(item => item !== licenseClass)
        : [...state.license_classes, licenseClass],
    });
  };

  const saveDriver = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) return false;

    await apiFetch(`${API_BASE}/drivers`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(form),
    });

    setForm({ name: '', phone: '', license_classes: [], notes: '' });
    loadDrivers();
  };

  const startEdit = (driver) => {
    setEditId(driver.id);
    setEditForm({
      name: driver.name || '',
      phone: driver.phone || '',
      license_classes: getLicenseClasses(driver),
      notes: driver.notes || '',
      is_active: driver.is_active !== false,
    });
  };

  const saveEdit = async (id) => {
    await apiFetch(`${API_BASE}/drivers/${id}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(editForm),
    });

    setEditId(null);
    loadDrivers();
  };

  const deleteDriver = async (id) => {
    if (!await showAppConfirm('Fahrer wirklich löschen?', {
      title: 'Fahrer löschen',
      confirmLabel: 'Löschen',
      danger: true,
    })) return;
    await apiFetch(`${API_BASE}/drivers/${id}`, { method: 'DELETE' });
    loadDrivers();
  };

  const renderLicensePicker = (state, setter) => (
    <div>
      <span style={styles.label}>Führerscheine</span>
      <div style={styles.licenseWrap}>
        {LICENSE_CLASSES.map(licenseClass => (
          <label key={licenseClass} style={styles.licenseChip}>
            <input
              type="checkbox"
              checked={state.license_classes.includes(licenseClass)}
              onChange={() => toggleLicense(state, setter, licenseClass)}
            />
            {licenseClass}
          </label>
        ))}
      </div>
    </div>
  );

  const isMobile = viewportWidth < 760;

  return (
    <ResourcePage title="Fahrer" description="Kontaktdaten und Führerscheine für die Disposition." paths={['/drivers']} reload={()=>loadDrivers()}>
      {editId!==null&&<ResourceEditor title="Fahrer bearbeiten" value={editForm} onClose={()=>setEditId(null)} onSave={()=>saveEdit(editId)}><Field label="Name">
                          <input style={styles.input} value={editForm.name} onChange={e => setEditForm({
  ...editForm,
  name: e.target.value
})} />
                        </Field><Field label="Telefon">
                          <input style={styles.input} value={editForm.phone} onChange={e => setEditForm({
  ...editForm,
  phone: e.target.value
})} />
                        </Field><Field label="Führerscheine">{renderLicensePicker(editForm, setEditForm)}</Field><Field label="Notizen">
                          <textarea style={mergeStyles(styles.textarea, {
  minHeight: '54px'
})} value={editForm.notes} onChange={e => setEditForm({
  ...editForm,
  notes: e.target.value
})} />
                        </Field><Field label="Status">
                          <label style={styles.activeToggle}>
                            <input type="checkbox" checked={editForm.is_active} onChange={e => setEditForm({
    ...editForm,
    is_active: e.target.checked
  })} />
                            Aktiv
                          </label>
                        </Field></ResourceEditor>}<CreatePanel title="Neuen Fahrer anlegen"><form style={mergeStyles(styles.formGrid, isMobile && { gridTemplateColumns: '1fr' })} onSubmit={saveDriver}>
          <input
            style={styles.input}
            placeholder="Name *"
            value={form.name}
            onChange={e => setForm({ ...form, name: e.target.value })}
            required
          />
          <input
            style={styles.input}
            placeholder="Telefon"
            value={form.phone}
            onChange={e => setForm({ ...form, phone: e.target.value })}
          />
          {renderLicensePicker(form, setForm)}
          <textarea
            style={styles.textarea}
            placeholder="Notizen"
            value={form.notes}
            onChange={e => setForm({ ...form, notes: e.target.value })}
          />
          <button style={styles.button} type="submit">Hinzufügen</button>
        </form></CreatePanel>

      <div style={styles.card}>
        <h2 style={styles.title}>Fahrer ({drivers.length})</h2>
        <div style={styles.tableWrap}>
          <table style={styles.table}>
            <thead>
              <tr>
                <th style={styles.th}>ID</th>
                <th style={styles.th}>Name</th>
                <th style={styles.th}>Telefon</th>
                <th style={styles.th}>Führerscheine</th>
                <th style={styles.th}>Notizen</th>
                <th style={styles.th}>Status</th>
                <th style={styles.th}>Aktion</th>
              </tr>
            </thead>
            <tbody>
              {drivers.map(driver => {
                const licenses = getLicenseClasses(driver);
                return (
                  <tr key={driver.id} style={driver.is_active === false ? styles.inactiveRow : undefined}>
                    <td style={styles.td}>{driver.id}</td>
                    {<>
                        <td style={styles.td}><strong>{driver.name}</strong></td>
                        <td style={styles.td}>{driver.phone || '-'}</td>
                        <td style={styles.td}>
                          {licenses.length > 0 ? licenses.map(item => <span key={item} style={styles.badge}>{item}</span>) : '-'}
                        </td>
                        <td style={styles.td}>{driver.notes || '-'}</td>
                        <td style={styles.td}>
                          <span style={driver.is_active === false ? styles.inactiveBadge : styles.activeBadge}>
                            {driver.is_active === false ? 'Inaktiv' : 'Aktiv'}
                          </span>
                        </td>
                      </>}
                    <td style={styles.td}>
                      {<>
                          <button style={styles.editBtn} onClick={() => startEdit(driver)}>Bearbeiten</button>
                          <button style={styles.deleteBtn} onClick={() => deleteDriver(driver.id).catch(reportResourceError)}>Löschen</button>
                        </>}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    </ResourcePage>
  );
}

export default Drivers;
